import { Card } from "@/components/layout";
import {
  PanelError,
  PanelLoading,
  SectionHeading,
} from "@/components/dashboard";
import { DonutChart, FunnelChart } from "@/components/charts";
import type { DonutSlice, DonutTone } from "@/components/charts";
import { useJourney } from "@/hooks/useJourney";
import { useFunnel } from "@/hooks/useFunnel";
import type { TimeRange } from "@/lib/time-range";

interface JourneyFunnelSectionProps {
  range: TimeRange;
}

const ABANDON_TONES: readonly DonutTone[] = [
  "red",
  "amber",
  "sky",
  "emerald",
  "slate",
];

function buildAbandonSlices(reasons: Record<string, number>): DonutSlice[] {
  const sorted = Object.entries(reasons).sort((a, b) => b[1] - a[1]);
  return sorted.map(([label, value], idx) => ({
    label,
    value,
    tone: ABANDON_TONES[idx % ABANDON_TONES.length],
  }));
}

export function JourneyFunnelSection({ range }: JourneyFunnelSectionProps) {
  const journey = useJourney(range);
  const funnel = useFunnel(range);

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Journey & Funnel"
        frs={["FR-03", "FR-04"]}
        source="log-analytics"
        description="End-to-end home_view → reservation_submitted funnel with stage drop-off."
      />

      <Card title="Funnel — counts & stage drop-off">
        {journey.isLoading ? (
          <PanelLoading height={220} />
        ) : journey.error ? (
          <PanelError
            error={journey.error}
            onRetry={() => journey.refetch()}
          />
        ) : (
          <FunnelChart stages={journey.data?.stages ?? []} />
        )}
      </Card>

      <Card title="Abandonment reasons">
        {funnel.isLoading ? (
          <PanelLoading height={140} />
        ) : funnel.error ? (
          <PanelError error={funnel.error} onRetry={() => funnel.refetch()} />
        ) : (
          <DonutChart
            slices={buildAbandonSlices(funnel.data?.abandonReasons ?? {})}
            centerLabel={
              funnel.data
                ? `${funnel.data.abandons.toLocaleString("en-US")} abandons`
                : undefined
            }
            emptyMessage="No abandonment data in the selected range"
          />
        )}
      </Card>
    </section>
  );
}
